import { Component, computed, OnInit, Signal } from '@angular/core';
import { MatCard, MatCardContent } from '@angular/material/card';
import { combineLatest, take } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DynamicObservable } from '../../../../core/classes/dynamic-observable';
import { DatatableComponent } from '../../../../shared/components/datatable/datatable.component';
import {
  ETableCrudScope,
  IDatatableColumn,
  IDatatableConfiguration,
} from '../../../../shared/components/datatable/datatable.interface';
import { IBaseDatatableState } from '../../../../state/base-datatable-store.service';
import { FormUtilitiesService } from '../../../../utilities/form-utilities.service';
import { BaseCrudPageComponent } from '../../../app/base-crud-page.component';
import { TableViewWidgetMappingsAddEditDialogComponent } from './table-view-widget-mappings-add-edit-dialog/table-view-widget-mappings-add-edit-dialog.component';
import { ITableViewMappingAddEditDialogData } from './table-view-widget-mappings-add-edit-dialog/table-view-widget-mappings-add-edit-dialog.interface';
import { ITableViewMapping, ITableViewMappingDatatableRow } from './table-view-widget-mappings.interface';
import { TableViewWidgetMappingsStore } from './table-view-widget-mappings.store';

@Component({
  imports: [DatatableComponent, MatCard, MatCardContent],
  providers: [TableViewWidgetMappingsStore],
  selector: 'table-view-widget-mappings',
  styleUrl: './table-view-widget-mappings.component.scss',
  templateUrl: './table-view-widget-mappings.component.html',
})
export class TableViewWidgetMappingsComponent
  extends BaseCrudPageComponent<ITableViewMapping, IBaseDatatableState<ITableViewMapping>>
  implements OnInit
{
  public readonly formattedDatatableRows: Signal<ITableViewMappingDatatableRow[]> = computed(() => {
    return this.store.data$().map((row) => ({
      ...row,
      endpoint: row.widgetRequest.name,
    }));
  });

  public readonly datatableColumns: IDatatableColumn<ITableViewMappingDatatableRow>[] = [
    { id: 'id', isSortable: false },
    { id: 'name', isSortable: true },
    { id: 'endpoint', isSortable: true },
  ];

  public readonly datatableConfigurations: IDatatableConfiguration<ITableViewMappingDatatableRow> = {
    addButtonText: this.addText,
    addScope: ETableCrudScope.single,
    dataRefreshEvent: this.setPageToOneAndRefreshDatatable$,
    deleteScope: ETableCrudScope.bulk,
    editScope: ETableCrudScope.single,
  };

  private readonly afterCreateUpdateSuccess = new DynamicObservable<unknown>(null);

  constructor(
    public readonly store: TableViewWidgetMappingsStore,
    public readonly formService: FormUtilitiesService,
  ) {
    super(store, {
      ambiguousNumberContext: 'field.ambiguousNumberOfTable',
      multiContext: 'field.tables',
      nameProperty: 'name',
      singleContext: 'field.table',
    });
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      combineLatest({
        afterCreateUpdateSuccess: this.afterCreateUpdateSuccess.obs$,
        datatable: this.datatableParams$.pipe(filter(Boolean)),
      }).subscribe(({ datatable }) => {
        const formattedSort =
          datatable.sort?.active === 'endpoint'
            ? { ...datatable.sort, ...{ active: 'widgetRequest' } }
            : datatable.sort;

        this.store.loadData({
          fields: ['name', 'widgetRequestId', 'map'],
          join: ['widgetRequest'],
          limit: datatable.limit,
          page: datatable.page,
          sort: [formattedSort],
          ...(datatable.search ? { search: { searchedFields: ['name'], searchText: datatable.search } } : {}),
        });
      }),
    );
  }

  public initializeAddEdit(isEdit: boolean): void {
    if (isEdit) {
      this.setupForEdit();
    } else {
      this.setupForAdd();
    }

    const data: ITableViewMappingAddEditDialogData = {
      ...(this.isEditDialog()
        ? {
            editData: this.selectedRows()[0],
          }
        : {}),
    };

    this.afterCreateUpdateSuccess.switch(
      this.matDialog
        .open(TableViewWidgetMappingsAddEditDialogComponent, {
          ...TableViewWidgetMappingsAddEditDialogComponent.dialogConfig,
          data,
        })
        .afterClosed()
        .pipe(take(1)),
    );
  }
}
